import { DatePicker } from "antd";
import dayjs from "dayjs";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useRef } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  chartOptions,
  doughnutChartOptions,
  horizontalChartOptions,
} from "./const";
import { calculateTotalLOS } from "./modules//calculateTotalLOS";
import { calculateTotalADR } from "./modules/calculateTotalADR";
import { calculateTotalLeadTime } from "./modules/calculateTotalLeadTime";
import { calculateTotalRoomNights } from "./modules/calculateTotalRoomNights";
import { calculateYoYChange } from "./modules/calculateYoYChange";
import { exportToExcel } from "./modules/exportToExcel";
import { exportToPDF } from "./modules/exportToPDF";
import { formatADRChartData } from "./modules/formatADRChartData";
import { formatBookingsChartData } from "./modules/formatBookingsChartData";
import { formatLOSChartData } from "./modules/formatLOSChartData";
import { formatLeadTimeChartData } from "./modules/formatLeadTimeChartData";
import { formatNationalityChartData } from "./modules/formatNationalityChartData";
import { formatPaymentMethodChartData } from "./modules/formatPaymentMethodChartData";
import { formatPromotionChartData } from "./modules/formatPromotionChartData";
import { formatRatePlanChartData } from "./modules/formatRatePlanChartData";
import { formatRevenueChartData } from "./modules/formatRevenueChartData";
import { formatRoomNightsChartData } from "./modules/formatRoomNightsChartData";
import { formatRoomTypeChartData } from "./modules/formatRoomTypeChartData";
import { getYoYTrend } from "./modules/getYoYTrend";
import { useAnalytics } from "./modules/useAnalytics";
import { CustomLegend } from "./molecules/customLegend";

// Register Chart.js components and the datalabels plugin
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ArcElement
);

const AnalyticsTemplate = () => {
  const chartRefs = {
    summary: useRef(null),
    bookings: useRef(null),
    roomNights: useRef(null),
    revenue: useRef(null),
    adr: useRef(null),
    los: useRef(null),
    leadTime: useRef(null),
    roomType: useRef(null),
    promotion: useRef(null),
    ratePlan: useRef(null),
    paymentMethod: useRef(null),
    bookedNationality: useRef(null),
    cancelledNationality: useRef(null),
  };
  const containerRef = useRef(null);
  const {
    fetchBookings,
    handleDateRangeChange,
    dataMode,
    setDataMode,
    bookings,
    previousYearBookings,
    loading,
    dateRange,
    setDateRange,
  } = useAnalytics();

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, dataMode]);

  const bookedBookings = useMemo(
    () => bookings.filter((b) => b.status === "Booked"),
    [bookings]
  );
  const cancelledBookings = useMemo(
    () => bookings.filter((b) => b.status === "cancelled"),
    [bookings]
  );
  const previousBookedBookings = useMemo(
    () => previousYearBookings.filter((b) => b.status === "Booked"),
    [previousYearBookings]
  );
  const previousCancelledBookings = useMemo(
    () => previousYearBookings.filter((b) => b.status === "cancelled"),
    [previousYearBookings]
  );

  // Current year metrics (memoized)
  const totalBookingsBooked = useMemo(
    () => bookedBookings.length,
    [bookedBookings]
  );
  const totalBookingsCancelled = useMemo(
    () => cancelledBookings.length,
    [cancelledBookings]
  );
  const totalRoomNightsBooked = useMemo(
    () => calculateTotalRoomNights(bookedBookings),
    [bookedBookings]
  );
  const totalRoomNightsCancelled = useMemo(
    () => calculateTotalRoomNights(cancelledBookings),
    [cancelledBookings]
  );
  const totalRevenueBooked = useMemo(
    () => bookedBookings.reduce((sum, booking) => sum + booking.totalamount, 0),
    [bookedBookings]
  );
  const totalRevenueCancelled = useMemo(
    () =>
      cancelledBookings.reduce((sum, booking) => sum + booking.totalamount, 0),
    [cancelledBookings]
  );
  const totalADRBooked = useMemo(
    () => calculateTotalADR(bookedBookings),
    [bookedBookings]
  );
  const totalADRCancelled = useMemo(
    () => calculateTotalADR(cancelledBookings),
    [cancelledBookings]
  );
  const totalLOSBooked = useMemo(
    () => calculateTotalLOS(bookedBookings),
    [bookedBookings]
  );
  const totalLOSCancelled = useMemo(
    () => calculateTotalLOS(cancelledBookings),
    [cancelledBookings]
  );
  const totalLeadTimeBooked = useMemo(
    () => calculateTotalLeadTime(bookedBookings),
    [bookedBookings]
  );
  const totalLeadTimeCancelled = useMemo(
    () => calculateTotalLeadTime(cancelledBookings),
    [cancelledBookings]
  );

  // Previous year metrics (memoized)
  const previousTotalBookingsBooked = useMemo(
    () => previousBookedBookings.length,
    [previousBookedBookings]
  );
  const previousTotalBookingsCancelled = useMemo(
    () => previousCancelledBookings.length,
    [previousCancelledBookings]
  );
  const previousTotalRoomNightsBooked = useMemo(
    () => calculateTotalRoomNights(previousBookedBookings),
    [previousBookedBookings]
  );
  const previousTotalRoomNightsCancelled = useMemo(
    () => calculateTotalRoomNights(previousCancelledBookings),
    [previousCancelledBookings]
  );
  const previousTotalRevenueBooked = useMemo(
    () =>
      previousBookedBookings.reduce(
        (sum, booking) => sum + booking.totalamount,
        0
      ),
    [previousBookedBookings]
  );
  const previousTotalRevenueCancelled = useMemo(
    () =>
      previousCancelledBookings.reduce(
        (sum, booking) => sum + booking.totalamount,
        0
      ),
    [previousCancelledBookings]
  );
  const previousTotalADRBooked = useMemo(
    () => calculateTotalADR(previousBookedBookings),
    [previousBookedBookings]
  );
  const previousTotalADRCancelled = useMemo(
    () => calculateTotalADR(previousCancelledBookings),
    [previousCancelledBookings]
  );
  const previousTotalLOSBooked = useMemo(
    () => calculateTotalLOS(previousBookedBookings),
    [previousBookedBookings]
  );
  const previousTotalLOSCancelled = useMemo(
    () => calculateTotalLOS(previousCancelledBookings),
    [previousCancelledBookings]
  );
  const previousTotalLeadTimeBooked = useMemo(
    () => calculateTotalLeadTime(previousBookedBookings),
    [previousBookedBookings]
  );
  const previousTotalLeadTimeCancelled = useMemo(
    () => calculateTotalLeadTime(previousCancelledBookings),
    [previousCancelledBookings]
  );

  // YoY Changes
  const yoyBookingsBooked = calculateYoYChange(
    totalBookingsBooked,
    previousTotalBookingsBooked
  );
  const yoyBookingsCancelled = calculateYoYChange(
    totalBookingsCancelled,
    previousTotalBookingsCancelled
  );
  const yoyRoomNightsBooked = calculateYoYChange(
    totalRoomNightsBooked,
    previousTotalRoomNightsBooked
  );
  const yoyRoomNightsCancelled = calculateYoYChange(
    totalRoomNightsCancelled,
    previousTotalRoomNightsCancelled
  );
  const yoyRevenueBooked = calculateYoYChange(
    totalRevenueBooked,
    previousTotalRevenueBooked
  );
  const yoyRevenueCancelled = calculateYoYChange(
    totalRevenueCancelled,
    previousTotalRevenueCancelled
  );
  const yoyADRBooked = calculateYoYChange(
    totalADRBooked,
    previousTotalADRBooked
  );
  const yoyADRCancelled = calculateYoYChange(
    totalADRCancelled,
    previousTotalADRCancelled
  );
  const yoyLOSBooked = calculateYoYChange(
    totalLOSBooked,
    previousTotalLOSBooked
  );
  const yoyLOSCancelled = calculateYoYChange(
    totalLOSCancelled,
    previousTotalLOSCancelled
  );
  const yoyLeadTimeBooked = calculateYoYChange(
    totalLeadTimeBooked,
    previousTotalLeadTimeBooked
  );
  const yoyLeadTimeCancelled = calculateYoYChange(
    totalLeadTimeCancelled,
    previousTotalLeadTimeCancelled
  );

  // YoY Trends
  const trendBookingsBooked = getYoYTrend(
    totalBookingsBooked,
    previousTotalBookingsBooked
  );
  const trendBookingsCancelled = getYoYTrend(
    totalBookingsCancelled,
    previousTotalBookingsCancelled
  );
  const trendRoomNightsBooked = getYoYTrend(
    totalRoomNightsBooked,
    previousTotalRoomNightsBooked
  );
  const trendRoomNightsCancelled = getYoYTrend(
    totalRoomNightsCancelled,
    previousTotalRoomNightsCancelled
  );
  const trendRevenueBooked = getYoYTrend(
    totalRevenueBooked,
    previousTotalRevenueBooked
  );
  const trendRevenueCancelled = getYoYTrend(
    totalRevenueCancelled,
    previousTotalRevenueCancelled
  );
  const trendADRBooked = getYoYTrend(totalADRBooked, previousTotalADRBooked);
  const trendADRCancelled = getYoYTrend(
    totalADRCancelled,
    previousTotalADRCancelled
  );
  const trendLOSBooked = getYoYTrend(totalLOSBooked, previousTotalLOSBooked);
  const trendLOSCancelled = getYoYTrend(
    totalLOSCancelled,
    previousTotalLOSCancelled
  );
  const trendLeadTimeBooked = getYoYTrend(
    totalLeadTimeBooked,
    previousTotalLeadTimeBooked
  );
  const trendLeadTimeCancelled = getYoYTrend(
    totalLeadTimeCancelled,
    previousTotalLeadTimeCancelled
  );

  const bookingsChartData = formatBookingsChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const roomNightsChartData = formatRoomNightsChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const revenueChartData = formatRevenueChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const adrChartData = formatADRChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const losChartData = formatLOSChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const leadTimeChartData = formatLeadTimeChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const roomTypeChartData = formatRoomTypeChartData(
    bookedBookings,
    cancelledBookings
  );
  const promotionChartData = formatPromotionChartData(
    bookedBookings,
    cancelledBookings
  );
  const ratePlanChartData = formatRatePlanChartData(
    bookedBookings,
    cancelledBookings
  );
  const paymentMethodChartData = formatPaymentMethodChartData(
    bookedBookings,
    cancelledBookings
  );
  const bookedNationalityChartData = formatNationalityChartData(
    bookedBookings,
    "Booked Nationalities"
  );
  const cancelledNationalityChartData = formatNationalityChartData(
    cancelledBookings,
    "Cancelled Nationalities"
  );

  const handleExportChange = (option) => {
    if (option === "pdf") {
      exportToPDF(containerRef, dateRange, dataMode, chartRefs);
    } else if (option === "excel") {
      exportToExcel(
        bookedBookings,
        cancelledBookings,
        dataMode,
        dateRange,
        previousBookedBookings,
        previousCancelledBookings
      );
    }
  };

  return (
    <div className="container-fluid p-4" ref={containerRef}>
      <h1 className="mb-3">Booking Analytics</h1>

      <div className="mb-4">
        <div className="row align-items-center mb-3">
          <div className="col-12 col-md-4 d-flex align-items-center mb-2 mb-md-0">
            {/* antd works with dayjs; dateRange state stays as Date objects */}
            <DatePicker.RangePicker
              value={[
                dateRange?.[0] ? dayjs(dateRange[0]) : null,
                dateRange?.[1] ? dayjs(dateRange[1]) : null,
              ]}
              onChange={(range) =>
                setDateRange(
                  [0, 1].map((i) => {
                    if (!range?.[i]) return null;
                    // Keep the previous time-of-day so fetchBookings' UTC normalization behaves as before
                    const prev = dateRange?.[i] ? dayjs(dateRange[i]) : dayjs();
                    return range[i]
                      .hour(prev.hour())
                      .minute(prev.minute())
                      .second(prev.second())
                      .millisecond(prev.millisecond())
                      .toDate();
                  })
                )
              }
              format="MM/DD/YYYY"
              allowClear={false}
              size="small"
              style={{ width: "100%" }}
            />
          </div>
          <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-center">
            <div className="d-flex align-items-center gap-3">
              <label className="tick-bullet d-flex align-items-center">
                <input
                  type="radio"
                  name="dataMode"
                  value="Booked Data"
                  checked={dataMode === "Booked Data"}
                  onChange={() => setDataMode("Booked Data")}
                  className="d-none"
                />
                <span className="tick-bullet-icon me-2"></span>
                <span style={{ fontSize: "0.9rem" }}>Booked Data</span>
              </label>
              <label className="tick-bullet d-flex align-items-center">
                <input
                  type="radio"
                  name="dataMode"
                  value="Stayed Data"
                  checked={dataMode === "Stayed Data"}
                  onChange={() => setDataMode("Stayed Data")}
                  className="d-none"
                />
                <span className="tick-bullet-icon me-2"></span>
                <span style={{ fontSize: "0.9rem" }}>Stayed Data</span>
              </label>
            </div>
          </div>
          <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-end">
            <div className="d-flex align-items-center gap-2">
              <select
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="form-select form-select-sm"
                defaultValue="Last Week"
                style={{ width: "150px", fontSize: "0.9rem" }}
              >
                <option value="Last Week">Last Week</option>
                <option value="Last Month">Last Month</option>
                <option value="Last 14 Days">Last 14 Days</option>
                <option value="Last 60 Days">Last 60 Days</option>
                <option value="Last 90 Days">Last 90 Days</option>
              </select>
              <select
                onChange={(e) => handleExportChange(e.target.value)}
                className="form-select form-select-sm"
                defaultValue=""
                disabled={loading || bookings.length === 0}
                style={{ width: "150px", fontSize: "0.9rem" }}
              >
                <option value="" disabled>
                  Export Options
                </option>
                <option value="pdf">Export to PDF</option>
                <option value="excel">Export to Excel</option>
              </select>
            </div>
          </div>
        </div>

        <div
          className="mt-4 bg-white p-3 rounded shadow-sm"
          ref={chartRefs.summary}
        >
          <h2 className="h5 fw-semibold mb-2">Booking Summary</h2>
          <i>
            (Booked/Cancelled){" "}
            {dataMode === "Booked Data" ? "booked" : "checked-in"} from{" "}
            <strong>
              {dateRange?.[0]
                ? dateRange[0].toLocaleDateString("vi-VN")
                : "N/A"}
            </strong>{" "}
            to{" "}
            <strong>
              {dateRange?.[1]
                ? dateRange[1].toLocaleDateString("vi-VN")
                : "N/A"}
            </strong>
          </i>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <p className="mb-0">
                Total Bookings:{" "}
                <span className="fw-medium text-primary">
                  {totalBookingsBooked.toLocaleString("vi-VN")}
                </span>
                {" / "}
                <span className="fw-medium text-danger">
                  {totalBookingsCancelled.toLocaleString("vi-VN")}
                </span>
                <br />
                <small className="text-muted">
                  YoY: {previousTotalBookingsBooked.toLocaleString("vi-VN")} /{" "}
                  {previousTotalBookingsCancelled.toLocaleString("vi-VN")} (
                  <span
                    style={{
                      color:
                        yoyBookingsBooked.rawPercentage === null
                          ? "black"
                          : yoyBookingsBooked.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendBookingsBooked} {yoyBookingsBooked.percentage}
                  </span>
                  {" / "}
                  <span
                    style={{
                      color:
                        yoyBookingsCancelled.rawPercentage === null
                          ? "black"
                          : yoyBookingsCancelled.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendBookingsCancelled} {yoyBookingsCancelled.percentage}
                  </span>
                  )
                </small>
              </p>
              <p className="mb-0">
                Total Room Nights:{" "}
                <span className="fw-medium text-purple">
                  {totalRoomNightsBooked.toLocaleString("vi-VN")}
                </span>
                {" / "}
                <span className="fw-medium text-danger">
                  {totalRoomNightsCancelled.toLocaleString("vi-VN")}
                </span>
                <br />
                <small className="text-muted">
                  YoY: {previousTotalRoomNightsBooked.toLocaleString("vi-VN")}{" "}
                  / {previousTotalRoomNightsCancelled.toLocaleString("vi-VN")}{" "}
                  (
                  <span
                    style={{
                      color:
                        yoyRoomNightsBooked.rawPercentage === null
                          ? "black"
                          : yoyRoomNightsBooked.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendRoomNightsBooked} {yoyRoomNightsBooked.percentage}
                  </span>
                  {" / "}
                  <span
                    style={{
                      color:
                        yoyRoomNightsCancelled.rawPercentage === null
                          ? "black"
                          : yoyRoomNightsCancelled.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendRoomNightsCancelled}{" "}
                    {yoyRoomNightsCancelled.percentage}
                  </span>
                  )
                </small>
              </p>
            </div>
            <div>
              <p className="mb-0">
                Total Revenue:{" "}
                <span className="fw-medium text-warning">
                  {totalRevenueBooked.toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}
                </span>
                {" / "}
                <span className="fw-medium text-danger">
                  {totalRevenueCancelled.toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}
                </span>
                <br />
                <small className="text-muted">
                  YoY:{" "}
                  {previousTotalRevenueBooked.toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}{" "}
                  /{" "}
                  {previousTotalRevenueCancelled.toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}{" "}
                  (
                  <span
                    style={{
                      color:
                        yoyRevenueBooked.rawPercentage === null
                          ? "black"
                          : yoyRevenueBooked.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendRevenueBooked} {yoyRevenueBooked.percentage}
                  </span>
                  {" / "}
                  <span
                    style={{
                      color:
                        yoyRevenueCancelled.rawPercentage === null
                          ? "black"
                          : yoyRevenueCancelled.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendRevenueCancelled} {yoyRevenueCancelled.percentage}
                  </span>
                  )
                </small>
              </p>
              <p className="mb-0">
                Total ADR:{" "}
                <span className="fw-medium text-info">
                  {Number(totalADRBooked).toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}
                </span>
                {" / "}
                <span className="fw-medium text-danger">
                  {Number(totalADRCancelled).toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}
                </span>
                <br />
                <small className="text-muted">
                  YoY:{" "}
                  {Number(previousTotalADRBooked).toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}{" "}
                  /{" "}
                  {Number(previousTotalADRCancelled).toLocaleString("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  })}{" "}
                  (
                  <span
                    style={{
                      color:
                        yoyADRBooked.rawPercentage === null
                          ? "black"
                          : yoyADRBooked.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendADRBooked} {yoyADRBooked.percentage}
                  </span>
                  {" / "}
                  <span
                    style={{
                      color:
                        yoyADRCancelled.rawPercentage === null
                          ? "black"
                          : yoyADRCancelled.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendADRCancelled} {yoyADRCancelled.percentage}
                  </span>
                  )
                </small>
              </p>
            </div>
            <div>
              <p className="mb-0">
                Total LOS:{" "}
                <span className="fw-medium text-success">
                  {Number(totalLOSBooked).toLocaleString("vi-VN")} Days
                </span>
                {" / "}
                <span className="fw-medium text-danger">
                  {Number(totalLOSCancelled).toLocaleString("vi-VN")} Days
                </span>
                <br />
                <small className="text-muted">
                  YoY:{" "}
                  {Number(previousTotalLOSBooked).toLocaleString("vi-VN")} /{" "}
                  {Number(previousTotalLOSCancelled).toLocaleString("vi-VN")}{" "}
                  Days (
                  <span
                    style={{
                      color:
                        yoyLOSBooked.rawPercentage === null
                          ? "black"
                          : yoyLOSBooked.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendLOSBooked} {yoyLOSBooked.percentage}
                  </span>
                  {" / "}
                  <span
                    style={{
                      color:
                        yoyLOSCancelled.rawPercentage === null
                          ? "black"
                          : yoyLOSCancelled.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendLOSCancelled} {yoyLOSCancelled.percentage}
                  </span>
                  )
                </small>
              </p>
              <p className="mb-0">
                Avg Lead Time:{" "}
                <span className="fw-medium text-secondary">
                  {Number(totalLeadTimeBooked).toLocaleString("vi-VN")} Days
                </span>
                {" / "}
                <span className="fw-medium text-danger">
                  {Number(totalLeadTimeCancelled).toLocaleString("vi-VN")}{" "}
                  Days
                </span>
                <br />
                <small className="text-muted">
                  YoY:{" "}
                  {Number(previousTotalLeadTimeBooked).toLocaleString(
                    "vi-VN"
                  )}{" "}
                  /{" "}
                  {Number(previousTotalLeadTimeCancelled).toLocaleString(
                    "vi-VN"
                  )}{" "}
                  Days (
                  <span
                    style={{
                      color:
                        yoyLeadTimeBooked.rawPercentage === null
                          ? "black"
                          : yoyLeadTimeBooked.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendLeadTimeBooked} {yoyLeadTimeBooked.percentage}
                  </span>
                  {" / "}
                  <span
                    style={{
                      color:
                        yoyLeadTimeCancelled.rawPercentage === null
                          ? "black"
                          : yoyLeadTimeCancelled.rawPercentage > 0
                          ? "green"
                          : "#B8860B",
                    }}
                  >
                    {trendLeadTimeCancelled} {yoyLeadTimeCancelled.percentage}
                  </span>
                  )
                </small>
              </p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-muted">Loading...</p>
      ) : bookings.length === 0 ? (
        <p className="text-center text-muted">
          No bookings found for the selected date range.
        </p>
      ) : (
        <>
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.bookings}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Bookings Over Time (Booked Date)"
                    : "Bookings Over Time (Check-in Date)"}
                </h2>
                <div style={{ height: "400px" }}>
                  <Bar
                    data={bookingsChartData}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: { text: "Bookings" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.roomNights}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Room Nights Over Time (Booked Date)"
                    : "Room Nights Over Time (Check-in Date)"}
                </h2>
                <div style={{ height: "400px" }}>
                  <Bar
                    data={roomNightsChartData}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: { text: "Room Nights" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.revenue}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Revenue Over Time (Booked Date)"
                    : "Revenue Over Time (Check-in Date)"}
                </h2>
                <div style={{ height: "400px" }}>
                  <Bar
                    data={revenueChartData}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: { text: "Revenue (VND)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.adr}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "ADR Over Time (Booked Date)"
                    : "ADR Over Time (Check-in Date)"}
                </h2>
                <div style={{ height: "400px" }}>
                  <Bar
                    data={adrChartData}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: { text: "ADR (VND)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.los}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "LOS Over Time (Booked Date)"
                    : "LOS Over Time (Check-in Date)"}
                </h2>
                <div style={{ height: "400px" }}>
                  <Bar
                    data={losChartData}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: { text: "LOS (Days)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.leadTime}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Lead Time Over Time (Booked Date)"
                    : "Lead Time Over Time (Check-in Date)"}
                </h2>
                <div style={{ height: "400px" }}>
                  <Bar
                    data={leadTimeChartData}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: { text: "Lead Time (Days)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.roomType}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Most Booked Room Types (Booked Date)"
                    : "Most Booked Room Types (Check-in Date)"}
                </h2>
                <div style={{ height: "300px" }}>
                  <Bar
                    data={roomTypeChartData}
                    options={{
                      ...horizontalChartOptions,
                      plugins: {
                        ...horizontalChartOptions.plugins,
                        title: { text: "Room Types (Count)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.promotion}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Most Used Promotions (Booked Date)"
                    : "Most Used Promotions (Check-in Date)"}
                </h2>
                <div style={{ height: "300px" }}>
                  <Bar
                    data={promotionChartData}
                    options={{
                      ...horizontalChartOptions,
                      plugins: {
                        ...horizontalChartOptions.plugins,
                        title: { text: "Promotions (Count)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.ratePlan}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Most Used Rate Plans (Booked Date)"
                    : "Most Used Rate Plans (Check-in Date)"}
                </h2>
                <div style={{ height: "300px" }}>
                  <Bar
                    data={ratePlanChartData}
                    options={{
                      ...horizontalChartOptions,
                      plugins: {
                        ...horizontalChartOptions.plugins,
                        title: { text: "Rate Plans (Count)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div
                className="bg-white p-3 rounded shadow-sm"
                ref={chartRefs.paymentMethod}
              >
                <h2 className="h6 fw-semibold mb-2">
                  {dataMode === "Booked Data"
                    ? "Most Used Payment Methods (Booked Date)"
                    : "Most Used Payment Methods (Check-in Date)"}
                </h2>
                <div style={{ height: "300px" }}>
                  <Bar
                    data={paymentMethodChartData}
                    options={{
                      ...horizontalChartOptions,
                      plugins: {
                        ...horizontalChartOptions.plugins,
                        title: { text: "Payment Methods (Count)" },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div
                className="bg-white p-3 rounded shadow-sm d-flex align-items-center"
                ref={chartRefs.bookedNationality}
              >
                <div className="text-center">
                  <h2 className="h6 fw-semibold mb-1">
                    Booked Nationalities
                  </h2>
                  {bookedNationalityChartData.labels.length === 0 ? (
                    <p className="text-muted small">
                      No booked bookings found.
                    </p>
                  ) : (
                    <div style={{ height: "300px" }}>
                      <Doughnut
                        data={bookedNationalityChartData}
                        options={{
                          ...doughnutChartOptions,
                          plugins: {
                            ...doughnutChartOptions.plugins,
                            title: {
                              text: "Booked Nationalities",
                              display: false,
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="ms-3 flex-grow-1">
                  {bookedNationalityChartData.labels.length > 0 && (
                    <CustomLegend chartData={bookedNationalityChartData} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div
                className="bg-white p-2 rounded shadow-sm d-flex align-items-center"
                ref={chartRefs.cancelledNationality}
              >
                <div className="text-center" style={{ flex: "0 0 150px" }}>
                  <h2 className="h6 fw-semibold mb-1">
                    Cancelled Nationalities
                  </h2>
                  {cancelledNationalityChartData.labels.length === 0 ? (
                    <p className="text-muted small">
                      No cancelled bookings found.
                    </p>
                  ) : (
                    <div style={{ height: "300px", width: "150px" }}>
                      <Doughnut
                        data={cancelledNationalityChartData}
                        options={{
                          ...doughnutChartOptions,
                          plugins: {
                            ...doughnutChartOptions.plugins,
                            title: {
                              text: "Cancelled Nationalities",
                              display: false,
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="ms-3 flex-grow-1">
                  {cancelledNationalityChartData.labels.length > 0 && (
                    <CustomLegend chartData={cancelledNationalityChartData} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsTemplate;
